import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { num } from "@owners/format";
import { api, useMe } from "../api";
import { PeopleTraining } from "../components/PeopleTraining";
import { useHotels } from "../components/Numbers";

/**
 * Team Members (SPEC §15). Live today: the Employee Workshop totals per hotel
 * (aggregates only — docs/WORKSHOP_FEED.md). The person-level roster
 * (anniversaries, new hires, overdue training, Recognize / Spif) needs a
 * roster feed and a decision on showing names to owners; until then Admins
 * see a SAMPLE roster of made-up people to show the intended design. Client
 * users never see the sample.
 */

type Filter = "all" | "anniversary" | "new" | "overdue";
interface Person { name: string; role: string; dept: string; hotel: number; hired: string; training: "On track" | "Overdue" | "Complete"; note: string; tags: Filter[] }

// Made-up people for the design demo. Hotel = index into the user's hotel list.
const SAMPLE: Person[] = [
  { name: "Maria Delgado", role: "Housekeeping Supervisor", dept: "Housekeeping", hotel: 0, hired: "2016-11-03", training: "Complete", note: "10-year anniversary Nov 3", tags: ["anniversary"] },
  { name: "James Whitfield", role: "Front Desk Agent", dept: "Front Office", hotel: 0, hired: "2026-09-15", training: "On track", note: "New hire · 6 weeks", tags: ["new"] },
  { name: "Aisha Rahman", role: "Assistant General Manager", dept: "Administration", hotel: 1, hired: "2021-11-12", training: "Complete", note: "5-year anniversary Nov 12", tags: ["anniversary"] },
  { name: "Carlos Mendez", role: "Maintenance Engineer", dept: "Engineering", hotel: 1, hired: "2023-04-02", training: "Overdue", note: "Safety refresher 14 days overdue", tags: ["overdue"] },
  { name: "Tanya Brooks", role: "Night Auditor", dept: "Front Office", hotel: 2, hired: "2026-08-20", training: "On track", note: "New hire · 10 weeks", tags: ["new"] },
  { name: "Derek Olsen", role: "Banquet Captain", dept: "Food & Beverage", hotel: 2, hired: "2019-11-25", training: "Overdue", note: "Alcohol service cert expires in 21 days", tags: ["overdue", "anniversary"] },
  { name: "Grace Kim", role: "Sales Manager", dept: "Sales", hotel: 3, hired: "2024-02-19", training: "Complete", note: "Recognized by guest review 10/21", tags: [] },
  { name: "Luis Ortega", role: "Room Attendant", dept: "Housekeeping", hotel: 3, hired: "2025-11-08", training: "On track", note: "1-year anniversary Nov 8", tags: ["anniversary"] },
  { name: "Priya Shah", role: "Front Office Manager", dept: "Front Office", hotel: 4, hired: "2022-06-30", training: "Complete", note: "Completed GM Academy module 3", tags: [] },
  { name: "Robert Hayes", role: "Breakfast Attendant", dept: "Food & Beverage", hotel: 4, hired: "2026-10-01", training: "On track", note: "New hire · 4 weeks", tags: ["new"] },
];
const OPEN_POSITIONS = [
  { role: "Chief Engineer", hotel: 1, days: 34 },
  { role: "Room Attendant (2)", hotel: 3, days: 12 },
  { role: "Night Auditor", hotel: 0, days: 8 },
];

const fmtDate = (s: string) => new Date(s + "T12:00:00Z").toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric", timeZone: "UTC" });

export function TeamMembers() {
  const me = useMe().data;
  const roster = useQuery({ queryKey: ["team-roster"], queryFn: () => api<RosterRes>("/api/team/roster?hotelIds=all") });
  const live = roster.data?.connected && roster.data.hotels.some((h) => h.members.length || h.openPositions.length);
  const headcount = roster.data?.hotels.reduce((t, h) => t + h.members.length, 0) ?? 0;
  return (
    <>
      <div className="eyebrow">Owners Module · Team Members</div>
      <h1>{headcount ? `${num(headcount)} Team Members in your care` : "Team Members"}</h1>
      <p className="muted" style={{ marginTop: -6 }}>The people in your hotels' care: training, certifications and coaching from the Employee Workshop.</p>
      <PeopleTraining endpoint="/api/team/workshop" title="Training & development" showWhenOff />
      {roster.isLoading ? <div className="card muted">Loading roster…</div>
        : live ? <LiveRoster d={roster.data!} />
        : me?.level === "admin" ? <SampleRoster /> : null}
    </>
  );
}

interface Member {
  id: string; name: string | null; title: string | null; department: string | null; hire_date: string | null; status: string | null;
  training: { assigned: number | null; completed: number | null; overdue: number | null };
  certifications: { active: number | null; expiring_60_days: number | null; expired: number | null };
}
interface RosterRes { connected: boolean; namesShown: boolean; hotels: Array<{ id: string; name: string; asOf: string | null; members: Member[]; openPositions: Array<{ title: string; department: string | null; posted_date: string | null }> }> }

const daysSince = (d: string, today: Date) => Math.floor((today.getTime() - new Date(d + "T12:00:00Z").getTime()) / 86_400_000);
/** Work anniversary within the next 30 days (or today), in whole years. */
function anniversary(hire: string | null, today: Date): { years: number; date: string } | null {
  if (!hire) return null;
  const h = new Date(hire + "T12:00:00Z");
  for (const y of [today.getUTCFullYear(), today.getUTCFullYear() + 1]) {
    const a = new Date(Date.UTC(y, h.getUTCMonth(), h.getUTCDate(), 12));
    const diff = (a.getTime() - today.getTime()) / 86_400_000;
    if (diff >= -1 && diff <= 30) { const years = y - h.getUTCFullYear(); return years >= 1 ? { years, date: a.toISOString().slice(0, 10) } : null; }
  }
  return null;
}

function LiveRoster({ d }: { d: RosterRes }) {
  const today = useMemo(() => new Date(), []);
  const [filter, setFilter] = useState<Filter>("all");
  const [hotel, setHotel] = useState("all");
  const people = d.hotels.flatMap((h) => h.members.filter((m) => !m.status || m.status === "active").map((m) => {
    const ann = anniversary(m.hire_date, today);
    const isNew = m.hire_date ? daysSince(m.hire_date, today) <= 90 && daysSince(m.hire_date, today) >= 0 : false;
    const overdue = (m.training.overdue ?? 0) > 0 || (m.certifications.expired ?? 0) > 0;
    return { m, hotelId: h.id, hotelName: h.name, ann, isNew, overdue };
  }));
  const inHotel = people.filter((p) => hotel === "all" || p.hotelId === hotel);
  const rows = inHotel.filter((p) => filter === "all" || (filter === "anniversary" && p.ann) || (filter === "new" && p.isNew) || (filter === "overdue" && p.overdue))
    .sort((a, b) => a.hotelName.localeCompare(b.hotelName) || (a.m.department ?? "").localeCompare(b.m.department ?? "") || (a.m.name ?? a.m.title ?? "").localeCompare(b.m.name ?? b.m.title ?? ""));
  const open = d.hotels.filter((h) => hotel === "all" || h.id === hotel).flatMap((h) => h.openPositions.map((o) => ({ ...o, hotelName: h.name })));
  const count = (f: (p: (typeof people)[number]) => unknown) => inHotel.filter(f).length;
  const chip = (f: Filter, label: string) => <button key={f} className={`btn ${filter === f ? "" : "secondary"}`} onClick={() => setFilter(f)}>{label}</button>;
  const withData = d.hotels.filter((h) => h.members.length || h.openPositions.length);
  const [openHotel, setOpenHotel] = useState<string | null>(null);
  const certsExpiring = people.filter((p) => (p.m.certifications.expiring_60_days ?? 0) > 0).length;
  const openAll = d.hotels.reduce((t, h) => t + h.openPositions.length, 0);
  const pick = (f: Filter) => { setFilter(f); document.getElementById("team-roster")?.scrollIntoView({ behavior: "smooth" }); };

  return (
    <>
    {/* Demo chips: what needs a look across the portfolio; click to filter the roster below. */}
    <div className="row team-chips">
      <button className={`chip${people.some((p) => p.ann) ? " hot" : ""}`} onClick={() => pick("anniversary")}>{num(people.filter((p) => p.ann).length)} anniversaries next 30 days</button>
      <button className={`chip${people.some((p) => p.isNew) ? " hot" : ""}`} onClick={() => pick("new")}>{num(people.filter((p) => p.isNew).length)} new hires (90 days)</button>
      <button className={`chip${people.some((p) => p.overdue) ? " hot" : ""}`} onClick={() => pick("overdue")}>{num(people.filter((p) => p.overdue).length)} with overdue training</button>
      <span className={`chip static${certsExpiring ? " hot" : ""}`}>{num(certsExpiring)} with certifications expiring (60 days)</span>
      <span className={`chip static${openAll ? " hot" : ""}`}>{num(openAll)} open positions</span>
      <span className="chip static" title="Employee engagement (eNPS) comes from the Art of Hospitality survey, not connected yet">eNPS · not connected</span>
    </div>

    <div className="card">
      <h2 style={{ marginTop: 0 }}>By hotel</h2>
      <table>
        <thead><tr><th>Hotel</th><th className="num">Team</th><th className="num">Open roles</th><th className="num">Trained</th><th className="num">Overdue</th><th className="num">Certs expiring</th><th className="num" title="Art of Hospitality survey — not connected yet">eNPS</th><th /></tr></thead>
        <tbody>{withData.map((h) => {
          const act = h.members.filter((m) => !m.status || m.status === "active");
          const asg = act.reduce((t, m) => t + (m.training.assigned ?? 0), 0), done = act.reduce((t, m) => t + (m.training.completed ?? 0), 0);
          const over = act.filter((m) => (m.training.overdue ?? 0) > 0).length, exp = act.filter((m) => (m.certifications.expiring_60_days ?? 0) > 0).length;
          const isOpen = openHotel === h.id;
          return [
            <tr key={h.id} className="clickable" onClick={() => setOpenHotel(isOpen ? null : h.id)}>
              <td><strong>{h.name}</strong></td>
              <td className="num">{num(act.length)}</td>
              <td className={`num${h.openPositions.length ? " bad" : ""}`}>{num(h.openPositions.length)}</td>
              <td className="num">{asg ? `${Math.round((done / asg) * 100)}%` : "—"}</td>
              <td className={`num${over ? " bad" : ""}`}>{num(over)}</td>
              <td className={`num${exp ? " bad" : ""}`}>{num(exp)}</td>
              <td className="num muted">—</td>
              <td className="muted" style={{ fontSize: 12 }}>{isOpen ? "▲ leaders" : "▼ leaders"}</td>
            </tr>,
            isOpen && <tr key={h.id + ":x"}><td colSpan={8} style={{ background: "var(--cream)" }}><LeaderCards members={act} namesShown={d.namesShown} today={today} /></td></tr>,
          ];
        })}</tbody>
      </table>
    </div>

    <div className="card" id="team-roster">
      <div className="row" style={{ justifyContent: "space-between", alignItems: "baseline" }}>
        <h2 style={{ margin: 0 }}>Team roster</h2>
        {!d.namesShown && <span className="muted" style={{ fontSize: 12 }}>Names are not shared in the Owners Module.</span>}
        {d.namesShown && <span className="pill warn-pill" title="Client users see titles only until names are approved for owners">Names visible to Admins only</span>}
      </div>
      <div className="row" style={{ margin: "10px 0" }}>
        <select value={hotel} onChange={(e) => setHotel(e.target.value)}>
          <option value="all">All hotels ({withData.length})</option>
          {withData.map((h) => <option key={h.id} value={h.id}>{h.name}</option>)}
        </select>
        {chip("all", `All · ${inHotel.length}`)}
        {chip("anniversary", `Anniversaries next 30 days · ${count((p) => p.ann)}`)}
        {chip("new", `New hires (90 days) · ${count((p) => p.isNew)}`)}
        {chip("overdue", `Overdue training · ${count((p) => p.overdue)}`)}
      </div>
      <div style={{ overflowX: "auto", maxHeight: 560, overflowY: "auto" }}>
        <table>
          <thead><tr><th>Team member</th><th>Hotel</th><th>Department</th><th>Hired</th><th className="num">Training</th><th>Certifications</th><th>Highlight</th></tr></thead>
          <tbody>{rows.slice(0, 300).map(({ m, hotelName, ann, isNew }) => (
            <tr key={m.id}>
              <td>{d.namesShown && m.name ? <><strong>{m.name}</strong><div className="muted" style={{ fontSize: 12 }}>{m.title ?? ""}</div></> : <strong>{m.title ?? "Team member"}</strong>}</td>
              <td>{hotelName}</td>
              <td>{m.department ?? "—"}</td>
              <td>{m.hire_date ? fmtDate(m.hire_date) : "—"}</td>
              <td className="num">
                {m.training.assigned ? `${num(m.training.completed ?? 0)} of ${num(m.training.assigned)}` : "—"}
                {(m.training.overdue ?? 0) > 0 && <div><span className="pill warn-pill">{num(m.training.overdue)} overdue</span></div>}
              </td>
              <td style={{ fontSize: 13 }}>
                {num(m.certifications.active ?? 0)} active
                {(m.certifications.expiring_60_days ?? 0) > 0 && <span className="bad"> · {num(m.certifications.expiring_60_days)} expiring</span>}
                {(m.certifications.expired ?? 0) > 0 && <span className="bad"> · {num(m.certifications.expired)} expired</span>}
              </td>
              <td style={{ fontSize: 13 }}>
                {ann && <>{ann.years}-year anniversary {fmtDate(ann.date).replace(/, \d{4}$/, "")}</>}
                {isNew && m.hire_date && <>New hire · {Math.max(1, Math.round(daysSince(m.hire_date, today) / 7))} weeks</>}
              </td>
            </tr>
          ))}</tbody>
        </table>
      </div>
      {rows.length > 300 && <p className="muted" style={{ fontSize: 12 }}>Showing 300 of {num(rows.length)}. Pick a hotel to narrow it.</p>}
      {rows.length === 0 && <p className="muted">No one matches.</p>}
      <h3>Open positions · {open.length}</h3>
      {open.length === 0 ? <p className="muted">None posted.</p> : (
        <table>
          <thead><tr><th>Role</th><th>Hotel</th><th>Department</th><th className="num">Days open</th></tr></thead>
          <tbody>{open.sort((a, b) => (a.posted_date ?? "").localeCompare(b.posted_date ?? "")).map((o, i) => (
            <tr key={i}><td>{o.title}</td><td>{o.hotelName}</td><td>{o.department ?? "—"}</td><td className="num">{o.posted_date ? num(daysSince(o.posted_date, today)) : "—"}</td></tr>
          ))}</tbody>
        </table>
      )}
      <p className="muted" style={{ fontSize: 12 }}>From the Employee Workshop{d.hotels.find((h) => h.asOf)?.asOf ? `, as of ${new Date(d.hotels.find((h) => h.asOf)!.asOf!).toLocaleString("en-US", { dateStyle: "medium", timeStyle: "short" })}` : ""}.</p>
    </div>
    </>
  );
}

const LEADER = /general manager|\bgm\b|director|manager|supervisor|chief|executive|lead\b/i;
/** Demo's leader cards: the hotel's managers and supervisors, with tenure and training. */
function LeaderCards({ members, namesShown, today }: { members: Member[]; namesShown: boolean; today: Date }) {
  const rank = (t: string) => (/general manager|\bgm\b/i.test(t) ? 0 : /director|chief|executive/i.test(t) ? 1 : /manager/i.test(t) ? 2 : 3);
  const leaders = members.filter((m) => LEADER.test(m.title ?? "")).sort((a, b) => rank(a.title ?? "") - rank(b.title ?? "") || (a.hire_date ?? "").localeCompare(b.hire_date ?? ""));
  if (!leaders.length) return <p className="muted" style={{ margin: 6 }}>No managers or supervisors on this hotel's roster.</p>;
  return (
    <div className="leader-cards">
      {leaders.map((m) => {
        const yrs = m.hire_date ? daysSince(m.hire_date, today) / 365.25 : null;
        return (
          <div key={m.id} className="leader-card">
            <div className="eyebrow">{m.department ?? "—"}</div>
            <div className="leader-title">{namesShown && m.name ? m.name : m.title ?? "Leader"}</div>
            {namesShown && m.name && <div className="muted" style={{ fontSize: 12 }}>{m.title}</div>}
            <div className="leader-stats">
              <span><strong>{yrs === null ? "—" : yrs < 1 ? `${Math.max(1, Math.round(yrs * 12))} mo` : `${yrs.toFixed(1)} yrs`}</strong> of service</span>
              <span><strong>{m.training.assigned ? `${num(m.training.completed ?? 0)}/${num(m.training.assigned)}` : "—"}</strong> training</span>
              {(m.training.overdue ?? 0) > 0 && <span className="bad"><strong>{num(m.training.overdue)}</strong> overdue</span>}
              {(m.certifications.expiring_60_days ?? 0) > 0 && <span className="bad"><strong>{num(m.certifications.expiring_60_days)}</strong> cert expiring</span>}
            </div>
          </div>
        );
      })}
    </div>
  );
}

function SampleRoster() {
  const hotels = useHotels().data?.hotels ?? [];
  const [filter, setFilter] = useState<Filter>("all");
  const [done, setDone] = useState<Record<string, string>>({});
  const hotelName = (i: number) => hotels.length ? hotels[i % hotels.length]!.name : `Hotel ${i + 1}`;
  const rows = SAMPLE.filter((p) => filter === "all" || p.tags.includes(filter));
  const count = (f: Filter) => SAMPLE.filter((p) => p.tags.includes(f)).length;
  const chip = (f: Filter, label: string) => (
    <button key={f} className={`btn ${filter === f ? "" : "secondary"}`} onClick={() => setFilter(f)}>{label}</button>
  );
  return (
    <div className="card" style={{ borderTop: "4px solid #b42318" }}>
      <div className="row" style={{ justifyContent: "space-between", alignItems: "baseline" }}>
        <h2 style={{ margin: 0 }}>Team roster</h2>
        <span className="pill bad-pill">SAMPLE DATA · Admins only</span>
      </div>
      <p className="muted" style={{ fontSize: 13 }}>
        Made-up people to show the planned roster. Client users don't see this card. The real roster needs a roster feed from the Employee Workshop
        and a decision on showing team member names to owners.
      </p>
      <div className="row" style={{ marginBottom: 10 }}>
        {chip("all", `All · ${SAMPLE.length}`)}
        {chip("anniversary", `Anniversaries this month · ${count("anniversary")}`)}
        {chip("new", `New hires · ${count("new")}`)}
        {chip("overdue", `Overdue training · ${count("overdue")}`)}
      </div>
      <div style={{ overflowX: "auto" }}>
        <table>
          <thead><tr><th>Team member</th><th>Hotel</th><th>Department</th><th>Hired</th><th>Training</th><th>Highlight</th><th></th></tr></thead>
          <tbody>{rows.map((p) => (
            <tr key={p.name}>
              <td><strong>{p.name}</strong><div className="muted" style={{ fontSize: 12 }}>{p.role}</div></td>
              <td>{hotelName(p.hotel)}</td>
              <td>{p.dept}</td>
              <td>{fmtDate(p.hired)}</td>
              <td><span className={`pill ${p.training === "Overdue" ? "warn-pill" : "ok-pill"}`}>{p.training}</span></td>
              <td style={{ fontSize: 13 }}>{p.note}</td>
              <td style={{ whiteSpace: "nowrap" }}>
                {done[p.name] ? <span className="muted" style={{ fontSize: 12 }}>{done[p.name]} (sample)</span> : <>
                  <button className="btn secondary" style={{ padding: "2px 8px" }} onClick={() => setDone({ ...done, [p.name]: "Recognized" })}>Recognize</button>{" "}
                  <button className="btn secondary" style={{ padding: "2px 8px" }} onClick={() => setDone({ ...done, [p.name]: "Spif sent" })}>Spif</button>
                </>}
              </td>
            </tr>
          ))}</tbody>
        </table>
      </div>
      <h3>Open positions</h3>
      <table>
        <thead><tr><th>Role</th><th>Hotel</th><th className="num">Days open</th></tr></thead>
        <tbody>{OPEN_POSITIONS.map((o) => <tr key={o.role + o.hotel}><td>{o.role}</td><td>{hotelName(o.hotel)}</td><td className="num">{o.days}</td></tr>)}</tbody>
      </table>
      <p className="muted" style={{ fontSize: 11 }}>Recognize and Spif only change this screen; nothing is sent or saved.</p>
    </div>
  );
}
